import { isNull, isSome } from '@mbfab/shared'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import type { ComponentPropsWithRef } from 'react'
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { dispatchNativeInputChange, mergeRefs } from '@/components/ui/clearable-input'
import { compactFormControlClass, formControlClass } from '@/components/ui/form'
import { useFormControlProps } from '@/components/ui/form-field-context'
import { cn } from '@/lib/utils'

// 使い方のルール: docs/develop/client_ui/components/form.md

// 日付入力は、ネイティブ <input type="date"> をそのまま残しつつ、自前のカレンダー
// ポップオーバーを右端のアイコンで添える方式にしている。
//
// 過去に HTMLInputElement.showPicker() でネイティブのカレンダーUIを開く実装（PR #316）を
// 試したが、ネイティブのピッカーは「開いている間キーボードフォーカスを奪う」仕様のため、
// カレンダーを開いたまま年月日セグメントへ数字を直接入力することができなかった
// （さらに Escape で常に blur していたため、開いたカレンダーを閉じて入力に戻ることもできず、
// 「カレンダー選択と直接入力の両立」という当初の狙いが実現できていなかった）。
// ネイティブ input 自体は残す（年月日セグメントの直接入力・自動送り・ロケール表示・
// モバイルのネイティブUI・スクリーンリーダー対応をそのまま使うため）。
// その上で自前のポップオーバーを添えることで、ポップオーバー側がフォーカスを奪わないように
// 作り込み、「開いたまま数字を打てる」を実現する

const DATE_FORMAT = 'YYYY-MM-DD'
const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']
const POPOVER_GAP = 4
const VIEWPORT_PADDING = 8
// ネイティブ <input type="date"> の「valid date string」は HTML 標準上、年が4桁以上あれば
// 成立する（年の桁数に上限が無い）ため、年セグメントに5〜6桁打っても入力自体はブラウザに
// 受理され、value としてそのまま返ってくる（例: "99999-09-15"）。バックエンドの GraphQL
// Date 型は4桁の年しか受け付けないため、この形からはみ出た値は DateInput の外に一切出さない
const STRICT_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function isOverflowingYearValue(rawValue: string): boolean {
  return rawValue !== '' && !STRICT_DATE_PATTERN.test(rawValue)
}

// value / defaultValue は type="date" である以上は文字列（YYYY-MM-DD）でしか使われないが、
// React の input 型定義上は number や readonly string[] も許容されているため、文字列以外は未設定として扱う
function toDateString(value: string | number | readonly string[] | undefined): string {
  return typeof value === 'string' ? value : ''
}

function parseStrictDate(value: string): Dayjs | null {
  if (value === '') {
    return null
  }
  const parsed = dayjs(value, DATE_FORMAT, true)
  return parsed.isValid() ? parsed : null
}

// カレンダーの週区切りグリッド（前月末・翌月頭の余白セルを含む6週分になることもある）を作る
function buildMonthGrid(monthAnchor: Dayjs): Dayjs[] {
  const startOfMonth = monthAnchor.startOf('month')
  const endOfMonth = monthAnchor.endOf('month')
  const gridStart = startOfMonth.subtract(startOfMonth.day(), 'day')
  const gridEnd = endOfMonth.add(6 - endOfMonth.day(), 'day')
  const dayCount = gridEnd.diff(gridStart, 'day') + 1
  return Array.from({ length: dayCount }, (_, index) => gridStart.add(index, 'day'))
}

type DateInputVariant = 'default' | 'compact'

type DateInputProps = Omit<ComponentPropsWithRef<'input'>, 'type' | 'min' | 'max'> & {
  variant?: DateInputVariant
  // JSX 上は data-* 属性として任意に渡せるが、カレンダーの trigger/本体/日セルの
  // data-testid を組み立てるために実体としても受け取れる形で明示しておく
  'data-testid'?: string
  // 幅・余白などレイアウトを決めるクラス（max-w-40 / min-w-0 / flex-1 / mt-3 等）はこちらへ渡す。
  // アイコンボタンはラッパー基準で絶対配置しているため、幅を決めるクラスを className（input側）に
  // 渡すと「input だけが狭まり、幅いっぱいに広がったラッパーの右端にアイコンが取り残される」
  // レイアウト崩れになる（2026/08/30 差分レビューで発覚）。className には高さ・角丸・パディング・
  // 文字サイズなど入力欄自体の見た目を決めるクラスだけを渡す
  wrapperClassName?: string
}

/**
 * 「提出日」「発行日」等の日付入力。年月日セグメントへの直接入力（キーボード）と、
 * 入力欄クリック／アイコンクリックで開く自前カレンダーからの選択が両方いつでも使える。
 * Enter で確定（blur）、Escape はまずカレンダーだけを閉じる（開いていなければ blur）。
 * 呼び出し元は従来どおり onBlur で保存する。
 * 年が4桁を超える値（ネイティブ input が受理してしまう）は blur/Enter 時点で直前の確定値へ
 * 戻し、呼び出し元の onChange / onBlur には一切渡さない（docs/issues/20260831_02）
 */
export function DateInput({
  variant = 'default',
  className,
  wrapperClassName,
  value,
  defaultValue,
  disabled,
  readOnly,
  style,
  onClick,
  onKeyDown,
  onChange,
  onBlur,
  ref: forwardedRef,
  'data-testid': testId,
  ...inputProps
}: DateInputProps) {
  // エラー状態（aria-invalid / aria-describedby）と id は FormField から受け取り、日付入力本体の <input> に付ける
  const rest = { ...inputProps, ...useFormControlProps(inputProps) }
  const internalRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const popoverId = useId()
  const ref = mergeRefs(internalRef, forwardedRef)

  const [open, setOpen] = useState(false)
  const [popoverPosition, setPopoverPosition] = useState<{ left: number; top: number } | null>(null)
  const [viewMonth, setViewMonth] = useState<Dayjs>(
    () => parseStrictDate(toDateString(value ?? defaultValue)) ?? dayjs(),
  )
  const [selectedValue, setSelectedValue] = useState(() => toDateString(value ?? defaultValue))
  const [lastControlledValue, setLastControlledValue] = useState(() => (isSome(value) ? toDateString(value) : null))

  if (isSome(value) && toDateString(value) !== lastControlledValue) {
    const nextValue = toDateString(value)
    const nextDate = parseStrictDate(nextValue)
    setLastControlledValue(nextValue)
    if (isSome(nextDate)) {
      setViewMonth(nextDate)
    }
  }

  // 制御コンポーネントでは value をそのまま選択表示へ使い、フォームリセット等にも
  // 追加レンダーなしで追従する。非制御時だけ入力イベントで管理した値を使う
  const visibleSelectedValue = isSome(value) ? toDateString(value) : selectedValue
  const isOpen = open && disabled !== true && readOnly !== true

  const updatePopoverPosition = useCallback(() => {
    const input = internalRef.current
    const popover = popoverRef.current
    if (isNull(input) || isNull(popover)) {
      return
    }

    const inputRect = input.getBoundingClientRect()
    const popoverRect = popover.getBoundingClientRect()
    const availableBelow = window.innerHeight - inputRect.bottom - VIEWPORT_PADDING
    const availableAbove = inputRect.top - VIEWPORT_PADDING
    const placeAbove = popoverRect.height > availableBelow && availableAbove > availableBelow
    const preferredTop = placeAbove ? inputRect.top - popoverRect.height - POPOVER_GAP : inputRect.bottom + POPOVER_GAP
    const maxTop = Math.max(VIEWPORT_PADDING, window.innerHeight - popoverRect.height - VIEWPORT_PADDING)
    const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - popoverRect.width - VIEWPORT_PADDING)

    setPopoverPosition({
      left: Math.min(Math.max(inputRect.left, VIEWPORT_PADDING), maxLeft),
      top: Math.min(Math.max(preferredTop, VIEWPORT_PADDING), maxTop),
    })
  }, [])

  const setPopoverNode = useCallback(
    (node: HTMLDivElement | null) => {
      popoverRef.current = node
      if (isSome(node)) {
        updatePopoverPosition()
      }
    },
    [updatePopoverPosition],
  )

  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    window.addEventListener('scroll', updatePopoverPosition, true)
    window.addEventListener('resize', updatePopoverPosition)
    return () => {
      window.removeEventListener('scroll', updatePopoverPosition, true)
      window.removeEventListener('resize', updatePopoverPosition)
    }
  }, [isOpen, updatePopoverPosition, viewMonth])

  function openCalendar() {
    if (disabled === true || readOnly === true) {
      return
    }
    // 開くたびに DOM 上の現在値（制御・非制御どちらでも実際の表示値）から表示月と
    // 選択ハイライトを作り直す。React state だけを見ると非制御コンポーネントの
    // 直接入力を取りこぼすため、必ず input.value を読む
    const current = internalRef.current?.value ?? ''
    setSelectedValue(current)
    setViewMonth(parseStrictDate(current) ?? dayjs())
    setPopoverPosition(null)
    setOpen(true)
  }

  function selectDay(dayString: string) {
    const input = internalRef.current
    if (isNull(input) || readOnly === true) {
      return
    }
    // React の value setter は素通りしないため、ネイティブ input イベントとして発火させ、
    // 制御・非制御どちらの呼び出し元でも onChange が通常のユーザー入力と同じ経路で呼ばれるようにする
    dispatchNativeInputChange(input, dayString)
    setSelectedValue(dayString)
    setOpen(false)
    input.focus()
  }

  const controlClass = variant === 'compact' ? compactFormControlClass : formControlClass
  // 呼び出し元が px-* 等で左右パディングを上書きしても、アイコン分の右余白だけは
  // 必ず確保する（cn は後勝ちなので className より後ろに置く）
  const iconPaddingClass = variant === 'compact' ? 'pr-8' : 'pr-9'

  return (
    <div className={cn('relative', wrapperClassName)}>
      <input
        ref={ref}
        type="date"
        disabled={disabled}
        readOnly={readOnly}
        value={value}
        defaultValue={defaultValue}
        data-testid={testId}
        // Firefox は WebKit の indicator 疑似要素を持たないため、ブラウザ固有の appearance を外して
        // 自前アイコンとの二重表示を防ぐ。Chrome/Safari 側は下の疑似要素指定で隠す
        style={{ MozAppearance: 'textfield', ...style }}
        className={cn(
          controlClass,
          // ネイティブのカレンダーアイコンは自前のアイコンボタンに置き換えるため隠す
          '[&::-webkit-calendar-picker-indicator]:hidden',
          className,
          iconPaddingClass,
        )}
        onClick={(event) => {
          onClick?.(event)
          openCalendar()
        }}
        onChange={(event) => {
          const nextValue = event.target.value
          if (isOverflowingYearValue(nextValue)) {
            // 年入力の途中で4桁を超えた瞬間の値は、呼び出し元へ転送しない（ここで setState
            // しなければ再描画は起きない）。この分岐後の挙動は制御/非制御で異なる。
            // 制御の呼び出し元（value を渡している場合）は、DOM の value を React が
            // 管理しているため、setState されずに value prop が変わらないと React の
            // 「controlled input の DOM 復元」が働き、直前の有効な value へ即座に
            // スナップバックする（4桁を超えた入力はそもそも表示にも残らない）。
            // 非制御の呼び出し元（defaultValue のみ）はこの復元が働かないため、ブラウザの
            // ネイティブ表示（ユーザーが打った桁）がそのまま残る。そちらは blur/Enter
            // 時点のガードで確定（保存）だけを止める
            return
          }
          onChange?.(event)
          const nextDate = parseStrictDate(nextValue)
          setSelectedValue(nextValue)
          if (isSome(nextDate)) {
            setViewMonth(nextDate)
          }
        }}
        onBlur={(event) => {
          if (isOverflowingYearValue(event.currentTarget.value)) {
            // blur/Enter 時点でも年が4桁を超えたままなら、呼び出し元の保存経路（onBlur）を
            // 一切呼ばずに直前の確定値（props の value。非制御なら mount 時の defaultValue）へ
            // 表示を戻す。dispatchNativeInputChange 経由で戻すと、その値は上の onChange
            // ハンドラを通常の入力と同じ経路で再度通るため、制御コンポーネントの呼び出し元にも
            // 正しい値が伝わる（不正値は一度も外に出ない）
            dispatchNativeInputChange(event.currentTarget, toDateString(value ?? defaultValue))
            setOpen(false)
            return
          }
          onBlur?.(event)
          setOpen(false)
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented) {
            return
          }
          if (event.key === 'Enter') {
            // ネイティブの type="date" はフォームの暗黙送信を引き起こしうるため止めたうえで、
            // blur を呼んで呼び出し元の onBlur 保存に揃える
            event.preventDefault()
            setOpen(false)
            event.currentTarget.blur()
          } else if (event.key === 'Escape') {
            if (isOpen) {
              // カレンダーだけ閉じて入力欄のフォーカスは維持する。ここで blur すると
              // 「カレンダーを閉じて入力に戻る」ができなくなる（過去の不具合の原因）
              setOpen(false)
            } else {
              event.currentTarget.blur()
            }
          }
          // それ以外のキーは数字の直接入力を壊さないよう一切邪魔しない
        }}
        {...rest}
      />
      <button
        type="button"
        aria-label={isOpen ? 'カレンダーを閉じる' : 'カレンダーを開く'}
        aria-expanded={isOpen}
        aria-controls={popoverId}
        data-testid={isSome(testId) ? `${testId}-calendar-trigger` : undefined}
        disabled={disabled === true || readOnly === true}
        // クリックでボタンにフォーカスが移ると入力欄が blur してポップオーバーが
        // 直後に閉じてしまうため、フォーカス移動そのものを止める
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        onClick={() => {
          internalRef.current?.focus()
          if (isOpen) {
            setOpen(false)
          } else {
            openCalendar()
          }
        }}
        className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
      >
        <Calendar className="size-3.5" />
      </button>
      {isOpen &&
        createPortal(
          <div
            id={popoverId}
            ref={setPopoverNode}
            // キーボード操作はネイティブ input の年月日セグメントに一本化し、
            // カレンダーはフォーカスを奪わないポインタ専用の補助UIとして提供する
            role="group"
            aria-label="日付を選択"
            data-testid={isSome(testId) ? `${testId}-calendar` : undefined}
            // ポップオーバー内のどこをクリックしても入力欄の focus/blur を発生させない。
            // フォーカスが移ると即座にポップオーバーが閉じてしまい、「開いたまま選択する」
            // 「開いたまま数字を打てる」のどちらも成立しなくなるため
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            // body portal は親の Base UI Dialog の DOM 外に出るため、pointerdown を既定動作済みにして
            // outside press として親 Dialog が閉じるのを防ぐ
            onPointerDown={(event) => {
              event.preventDefault()
            }}
            style={{
              left: popoverPosition?.left ?? 0,
              top: popoverPosition?.top ?? 0,
              visibility: isSome(popoverPosition) ? 'visible' : 'hidden',
            }}
            className="fixed z-50 w-64 rounded-lg border border-border bg-card p-3 shadow-lg"
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label="前月"
                data-testid={isSome(testId) ? `${testId}-prev-month` : undefined}
                onClick={() => {
                  setViewMonth((current) => current.subtract(1, 'month'))
                }}
                className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="text-sm font-bold text-foreground">{viewMonth.format('YYYY年M月')}</span>
              <button
                type="button"
                aria-label="翌月"
                data-testid={isSome(testId) ? `${testId}-next-month` : undefined}
                onClick={() => {
                  setViewMonth((current) => current.add(1, 'month'))
                }}
                className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-y-1 text-center text-xs font-medium text-muted-foreground">
              {WEEKDAY_LABELS.map((label) => (
                <div key={label}>{label}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1">
              {buildMonthGrid(viewMonth).map((day) => {
                const dayString = day.format(DATE_FORMAT)
                const inCurrentMonth = day.month() === viewMonth.month()
                const isToday = day.isSame(dayjs(), 'day')
                const isSelected = dayString === visibleSelectedValue
                return (
                  <button
                    key={dayString}
                    type="button"
                    aria-label={day.format('YYYY年M月D日')}
                    aria-current={isToday ? 'date' : undefined}
                    aria-pressed={isSelected}
                    data-testid={isSome(testId) ? `${testId}-day-${dayString}` : undefined}
                    onClick={() => {
                      selectDay(dayString)
                    }}
                    className={cn(
                      'rounded-md py-1 text-xs tabular-nums text-foreground',
                      !inCurrentMonth && 'text-muted-foreground/50',
                      isToday && !isSelected && 'font-bold text-primary',
                      isSelected && 'bg-primary text-primary-foreground hover:bg-primary',
                      !isSelected && 'hover:bg-secondary',
                    )}
                  >
                    {day.date()}
                  </button>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

/** 絞り込み・印刷設定向けの高密度な日付入力。CompactFormInput と見た目・挙動を揃える */
export function CompactDateInput(props: Omit<DateInputProps, 'variant'>) {
  return <DateInput variant="compact" {...props} />
}
